import { createElement } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { Car, Footprints, TrainFront, Plane } from "lucide-react";
import { getIcon } from "@/components/iconMap";

export const AIRPOG_LOGO = "https://static.prod-images.emergentagent.com/jobs/301a1c64-0b19-462c-acf8-1e89c9bc05f1/images/cba60e89c5f18aec88b7b5e4bb560a3a3cd16137942d429c9dc4648d7082b353.png";

// ---- mockup-style teardrop map pins ----
const PIN_PALETTE = ["#7C3AED", "#6366F1", "#8B5CF6", "#4F46E5", "#2563EB"];
export function pinColor(b) {
  if (b.is_sale) return "#D946EF";
  if (b.featured) return "#F59E0B";
  if (b.has_deals) return "#2563EB";
  let h = 0;
  for (const c of String(b.id || "")) h = (h * 31 + c.charCodeAt(0)) | 0;
  return PIN_PALETTE[Math.abs(h) % PIN_PALETTE.length];
}
const _pinCache = {};
export function bizPinHtml(b, active, marked, hot, hiring) {
  const atom = !!b.atomic_pog; // ⚛️ ATOMIC POG — the apex pin, guarded by POGZILLA
  const sup = !!b.super_pog; // 🚀 SUPER POG merchants get the SQUARE premium pin
  // 🍋 LITTLE GIANTS — kid-run stands get their own emoji pins
  const kid = b.category_id === "personal_sale" ? ({ "Lemonade Stand": "🍋", "Bake Sale": "🧁", "Kids Car Wash": "🫧", "Craft Stand": "🎨" })[b.sale_type] : null;
  const gl = !!b.golden_lemon; // 👑 busiest kid stand wears the golden lemon crown
  const color = atom ? "#E879F9" : sup ? "#FBBF24" : gl ? "#FBBF24" : kid ? "#FDE047" : pinColor(b);
  const nl = (!!hot || !!b.has_deals) && ["bar_pub", "liquor", "casino_gaming", "theater_cinema", "fast_food", "restaurants_food"].includes(b.category_id || "");
  const key = `${b.icon || "_"}|${color}|${active ? 1 : 0}|${marked ? 1 : 0}|${hot ? 1 : 0}|${hiring ? 1 : 0}|${sup ? 1 : 0}|${atom ? 1 : 0}|${nl ? 1 : 0}|${kid || "_"}|${gl ? 1 : 0}`;
  if (_pinCache[key]) return _pinCache[key];
  const w = active ? 34 : 29;
  const h = active ? 46 : 39;
  const Ico = b.icon ? getIcon(b.icon) : null;
  const glyph = Ico
    ? renderToStaticMarkup(createElement(Ico, { width: 14, height: 14, color: atom ? "#A21CAF" : sup ? "#B45309" : color, strokeWidth: 2.6 }))
    : "";
  const inner = kid
    ? `<div style="position:absolute;top:${active ? 5 : 4}px;left:0;right:0;display:flex;justify-content:center;font-size:${active ? 15 : 13}px;">${kid}</div>`
    : glyph
    ? `<div style="position:absolute;top:${sup ? (active ? 7 : 6) : active ? 8 : 7}px;left:0;right:0;display:flex;justify-content:center;">${glyph}</div>`
    : `<div style="position:absolute;top:${active ? 10 : 9}px;left:50%;transform:translateX(-50%);width:8px;height:8px;border-radius:50%;background:${color};"></div>`;
  const star = marked
    ? `<div class="airpin-star" style="position:absolute;top:-7px;right:-7px;font-size:12px;filter:drop-shadow(0 1px 2px rgba(0,0,0,.65));">⭐</div>`
    : "";
  const flame = hot
    ? `<div class="airpin-hot" style="position:absolute;top:-7px;left:-7px;font-size:12px;filter:drop-shadow(0 1px 2px rgba(0,0,0,.65));">🔥</div>`
    : "";
  const briefcase = hiring
    ? `<div class="airpin-hiring" style="position:absolute;bottom:-4px;right:-8px;font-size:11px;filter:drop-shadow(0 1px 2px rgba(0,0,0,.65));">💼</div>`
    : "";
  const rocket = atom
    ? `<div class="airpin-atomic" style="position:absolute;top:-9px;right:-9px;font-size:13px;filter:drop-shadow(0 1px 2px rgba(0,0,0,.65));">🐲</div>`
    : sup
    ? `<div class="airpin-super" style="position:absolute;top:-8px;right:-8px;font-size:12px;filter:drop-shadow(0 1px 2px rgba(0,0,0,.65));">🚀</div>`
    : gl
    ? `<div class="airpin-golden" style="position:absolute;top:-9px;right:-9px;font-size:12px;filter:drop-shadow(0 1px 2px rgba(0,0,0,.65));">👑</div>`
    : "";
  const shape = sup
    ? `<svg width="${w}" height="${h}" viewBox="0 0 24 32" xmlns="http://www.w3.org/2000/svg">
      <rect x="0.5" y="0.5" width="23" height="23" rx="6" fill="${color}"/>
      <path d="M8 23 L12 31.5 L16 23 Z" fill="${color}"/>
      <rect x="3.5" y="3.5" width="17" height="17" rx="4" fill="#fff"/>
    </svg>`
    : `<svg width="${w}" height="${h}" viewBox="0 0 24 32" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 0C5.37 0 0 5.37 0 12c0 8.6 12 20 12 20s12-11.4 12-20C24 5.37 18.63 0 12 0z" fill="${color}"/>
      <circle cx="12" cy="12" r="8" fill="#fff"/>
    </svg>`;
  const html = `<div class="airpin${active ? " active" : ""}${nl ? " airpin-nl" : ""}" style="position:relative;width:${w}px;height:${h}px;filter:drop-shadow(0 3px 4px rgba(15,23,42,.4));">
    ${shape}${inner}${star}${flame}${briefcase}${rocket}</div>`;
  _pinCache[key] = html;
  return html;
}

export const AIRPOG_ICON = "/airpog-icon.png";
export const POGZILLA_HERO = "/pogzilla-hero.png";
export const POGZILLA_BADGE = "/pogzilla.png";
// 🦖 Dyno Stampede — a herd of POGZILLAs charge across at staggered sizes/speeds.
export const DYNO_HERD = [
  { w: "9rem",  bottom: "5rem",    dur: 3.4, delay: 0,    jump: 22 },
  { w: "5.5rem", bottom: "8.5rem", dur: 2.8, delay: 0.18, jump: 15 },
  { w: "7rem",  bottom: "6.5rem",  dur: 3.1, delay: 0.4,  jump: 18 },
  { w: "4.5rem", bottom: "10rem",  dur: 2.5, delay: 0.6,  jump: 11 },
  { w: "8rem",  bottom: "5.5rem",  dur: 3.6, delay: 0.8,  jump: 20 },
];

export const DEFAULT_POS = { lat: 40.758, lng: -73.9855 }; // Times Square fallback
export const RADIUS_OPTIONS = [1, 5, 10, 50, 100];
export const TILE_URLS = {
  day: "https://tile.openstreetmap.org/{z}/{x}/{y}.png",
  night: "https://tile.openstreetmap.org/{z}/{x}/{y}.png",
  twilight: "https://tile.openstreetmap.org/{z}/{x}/{y}.png",
  blackout: "https://tile.openstreetmap.org/{z}/{x}/{y}.png",
  satellite: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
};
export const THEME_CYCLE = { day: "twilight", twilight: "satellite", satellite: "night", night: "blackout", blackout: "day" };
// 🌆 OSM tiles are light — night/twilight get their dark looks via CSS filters (Carto now requires an API key).
export const THEME_FILTERS = {
  night: "invert(1) hue-rotate(185deg) brightness(0.82) contrast(1.14) saturate(1.45)",
  twilight: "invert(1) hue-rotate(215deg) brightness(1.18) contrast(0.9) saturate(1.05) sepia(0.12)",
  blackout: "brightness(0)",
};

// AIR.POG works anywhere — not just driving. Each travel mode tunes the scan
// radius + copy so the deals strip fits how you're actually moving.
export const TRAVEL_MODES = {
  drive:   { label: "Drive",   short: "Driving",      icon: Car,         radius: 10, hint: "Live deals as you roll" },
  walk:    { label: "Walk",    short: "On a walk",    icon: Footprints,  radius: 1,  hint: "Deals within a short walk" },
  transit: { label: "Transit", short: "On transit",   icon: TrainFront,  radius: 5,  hint: "Deals along your ride" },
  fly:     { label: "Fly",     short: "Arriving soon", icon: Plane,      radius: 50, hint: "Preview deals at your destination" },
};
export const TRAVEL_ORDER = ["drive", "walk", "transit", "fly"];

// 🎈 BALLOON FEST fleet — balloons drift up the map at staggered lanes/speeds.
export const BALLOON_FLEET = [
  { img: "/airpogs/skyloon.webp",   w: "4.5rem", left: "6%",  dur: 9,    delay: 0,    sway: 26 },
  { img: "/airpogs/heartloon.webp", w: "5.5rem", left: "24%", dur: 11,   delay: 0.8,  sway: 34 },
  { img: "/airpogs/zigloon.webp",   w: "4rem",   left: "42%", dur: 8.4,  delay: 1.6,  sway: 22 },
  { img: "/airpogs/goldloon.webp",  w: "6.5rem", left: "58%", dur: 12.5, delay: 0.4,  sway: 30 },
  { img: "/airpogs/patchloon.webp", w: "4.8rem", left: "74%", dur: 9.8,  delay: 2.2,  sway: 28 },
  { img: "/airpogs/stormloon.webp", w: "5rem",   left: "86%", dur: 10.6, delay: 1.2,  sway: 24 },
  { img: "/airpogs/duskloon.webp",  w: "3.8rem", left: "14%", dur: 8,    delay: 3,    sway: 20 },
];

// POG type presets (free-form custom also allowed). Consumers drop these on the map.
export const POG_PRESETS = [
  "Garage Sale", "Estate Sale", "Yard Sale", "Lemonade Stand", "Bake Sale",
  "Kids Car Wash", "Craft Stand", "Party POG", "Christmas POG",
  "Rave POG", "Charity POG", "Meetup", "Event", "Auction", "Shout-out",
];

// Turn a seller's free-form contact (phone / email / @handle / link) into an action.
export function contactAction(raw) {
  const c = (raw || "").trim();
  if (!c) return null;
  if (/^https?:\/\//i.test(c)) return { href: c, label: "Message seller", copy: false };
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(c)) return { href: `mailto:${c}?subject=AIR.POG delivery request`, label: "Email · request delivery", copy: false };
  const digits = c.replace(/[^\d+]/g, "");
  if (digits.length >= 7 && /^[+\d]/.test(c)) return { href: `sms:${digits}`, label: "Text · request delivery", copy: false };
  if (/^@/.test(c)) return { href: null, label: `Contact ${c} (tap to copy)`, copy: true, value: c };
  return { href: null, label: "Copy seller contact", copy: true, value: c };
}

export function bearingDeg(a, b) {
  const toRad = (d) => (d * Math.PI) / 180;
  const dLon = toRad(b.lng - a.lng);
  const y = Math.sin(dLon) * Math.cos(toRad(b.lat));
  const x = Math.cos(toRad(a.lat)) * Math.sin(toRad(b.lat)) - Math.sin(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.cos(dLon);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

export const GPS_APPS = {
  google: { label: "Google Maps", url: (lat, lng) => `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}` },
  waze: { label: "Waze", url: (lat, lng) => `https://waze.com/ul?ll=${lat},${lng}&navigate=yes` },
  apple: { label: "Apple Maps", url: (lat, lng) => `https://maps.apple.com/?daddr=${lat},${lng}` },
  smarttruck: { label: "SmartTruckRoute / Truck GPS", url: (lat, lng) => `geo:${lat},${lng}?q=${lat},${lng}(Destination)` },
};
